import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { handleModalEscape, pushModalEscape } from '../../../../lib/modal-escape';
import { Button, Card, IconButton, Typography } from '../../../symphony/ui';
import { EarnCreditsOffer } from '../../../symphony/earn-credits/EarnCreditsOffer';
import { SETTINGS_PAGE } from '../settings-shared';
import { BuyCreditsProps, useBuyCredits } from './use-buy-credits';
import styles from './BuyCreditsSheet.module.css';

// The mobile "Buy more credits" bottom sheet (Figma 12252-58241): a bottom-anchored card with a drag
// handle over a full-window scrim, sliding up on open. Only the layout differs from the desktop
// BuyCreditsModal — the copy, the BI and the checkout hand-off are the shared useBuyCredits. Rendered
// only from the narrow settings layout (SettingsMobile/PlanCard).
export function BuyCreditsSheet({ open, onClose, topup, planName, checkoutHref }: BuyCreditsProps) {
  const vm = useBuyCredits({ onClose, topup, planName, checkoutHref });
  const sheetRef = useRef<HTMLDivElement>(null);
  // Read `close` through a ref so a fresh handler each render doesn't re-register the escape stack.
  const closeRef = useRef(vm.close);
  closeRef.current = vm.close;

  // Slide up from the bottom on open, like a pushed screen — WAAPI commits the off-screen start
  // keyframe atomically (no flash), runs on the compositor, and is skipped under reduced motion.
  useEffect(() => {
    if (!open) {
      return;
    }
    const el = sheetRef.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    const anim = el.animate([{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], {
      duration: 300,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    });
    return () => anim.cancel();
  }, [open]);

  // Escape closes THIS sheet (capture-phase, innermost-first), the same contract the DS Modal uses.
  useEffect(() => {
    if (!open) {
      return;
    }
    const pop = pushModalEscape(() => closeRef.current());
    const onKey = (e: KeyboardEvent) => handleModalEscape(e);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      pop();
    };
  }, [open]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className={styles.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          vm.close();
        }
      }}
    >
      <div ref={sheetRef} className={styles.sheet} role="dialog" aria-modal="true" aria-labelledby="buy-credits-title">
        <div className={styles.handle} aria-hidden />
        <div className={styles.head}>
          <div className={styles.titleRow}>
            <Typography variant="h2" component="h2" id="buy-credits-title">
              {vm.title}
            </Typography>
            <IconButton variant="text" size="small" aria-label={vm.closeLabel} onClick={vm.close}>
              <X size={24} aria-hidden />
            </IconButton>
          </div>
          <Typography variant="p2" component="p" color="secondary">
            {vm.subtitle}
          </Typography>
        </div>

        <Card variant="stone" className={styles.pack}>
          <div className={styles.packContent}>
            <div className={styles.row}>
              <Typography variant="h3" component="span">
                {vm.amountLabel}
              </Typography>
              <Typography variant="h3" component="span">
                {vm.priceLabel}
              </Typography>
            </div>
            <Typography variant="p3" component="div" color="secondary" align="end">
              {vm.oneTimeLabel}
            </Typography>
          </div>
        </Card>

        <Button fullWidth href={vm.checkoutHref} onClick={vm.onCheckout}>
          {vm.checkoutLabel}
        </Button>

        {/* The premium path: a top-tier user has no upgrade left to cannibalize, so the free way to
            more credits is offered under Check out — which keeps its primary skin and its own BI. */}
        <EarnCreditsOffer
          entry={{ kind: 'buy_credits' }}
          pageName={SETTINGS_PAGE.page_name}
          variant="mobile"
          className={styles.earnOffer}
        />
      </div>
    </div>,
    document.body,
  );
}
